import { pageScrollBehavior } from "@/lib/scroll";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Coins, LayoutDashboard, Menu } from "lucide-react";
import { BrandLogo } from "@/components/ui/brand-logo";
import { useSecureAuth } from "@/hooks/useSecureAuth";
import { AuthDialog } from "@/components/AuthDialog";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useLocale } from "@/i18n/context";
import { dashboardPath, isAppAreaPath, localizedPath } from "@/i18n/routes";
import { useCreditBalance } from "@/features/dashboard";
import { cn } from "@/lib/utils";

/** Landing-page section ids the nav links scroll to, in document order. */
const SECTION_IDS = ["features", "how-it-works", "pricing"] as const;

export const Header = () => {
  const { user, loading } = useSecureAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [authDialogMode, setAuthDialogMode] = useState<'login' | 'register'>('login');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string>("");
  const { data: creditBalance = 0 } = useCreditBalance();
  const { t, locale } = useLocale();
  const homePath = localizedPath("home", locale);
  const userDashboardPath = dashboardPath(locale);
  const isAppRoute = isAppAreaPath(location.pathname);

  const navItems = [
    { id: "features", label: t.header.features },
    { id: "how-it-works", label: t.header.howItWorks },
    { id: "pricing", label: t.header.pricing },
  ];

  // Scroll-spy: highlight the nav link for the section currently in view.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (location.pathname !== homePath) {
      setActiveSection("");
      return;
    }

    const sections = SECTION_IDS.map((id) => document.getElementById(id)).filter(
      (el): el is HTMLElement => el !== null,
    );
    if (sections.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const inView = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (inView[0]) setActiveSection(inView[0].target.id);
      },
      { rootMargin: "-45% 0px -45% 0px", threshold: 0 },
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [location.pathname, homePath]);

  const handleNavClick = (sectionId: string) => {
    if (location.pathname === homePath) {
      // Already on homepage, just scroll
      document.getElementById(sectionId)?.scrollIntoView({ behavior: pageScrollBehavior() });
    } else {
      // Navigate to homepage first, then scroll after the route change
      navigate(homePath);
      setTimeout(() => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: pageScrollBehavior() });
      }, 100);
    }
  };

  const scrollToPricing = () => {
    handleNavClick('pricing');
  };


  // Ensure consistent rendering between server and client
  if (loading) {
    return (
      <header className="fixed top-0 w-full glass-header brand-header z-50" suppressHydrationWarning>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <Link to={homePath} className="flex items-center gap-2" aria-label="nreport">
              <BrandLogo />
            </Link>
            
            {/* Desktop Navigation */}
            {!isAppRoute && (
              <nav className="hidden lg:flex items-center gap-1">
                {navItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleNavClick(item.id)}
                    className="rounded-lg px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  >
                    {item.label}
                  </button>
                ))}
              </nav>
            )}
            
            <div className="flex items-center gap-3">
              {/* Loading placeholder */}
              <div className="h-9 w-20 bg-secondary/30 animate-pulse rounded" />
            </div>
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className="fixed top-0 w-full glass-header brand-header z-50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <Link to={homePath} className="flex items-center gap-2" aria-label="nreport">
            <BrandLogo />
          </Link>
          
          {/* Desktop Navigation */}
          {!isAppRoute && (
            <nav className="hidden lg:flex items-center gap-1">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item.id)}
                  aria-current={activeSection === item.id ? "true" : undefined}
                  className={cn(
                    "rounded-lg px-4 py-2 text-sm font-medium transition-colors",
                    activeSection === item.id
                      ? "bg-primary/[0.06] text-primary"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground",
                  )}
                >
                  {item.label}
                </button>
              ))}
            </nav>
          )}
          
          <div className="flex items-center gap-2 sm:gap-3">
            {!isAppRoute && <LanguageSwitcher />}
            {user ? (
              <>
                {/* Credit Balance - Mobile Optimized */}
                <div className="flex items-center gap-2 rounded-full border border-primary/15 bg-primary/5 px-2.5 sm:px-3 py-1">
                  <Coins className="h-4 w-4 text-primary" />
                  <span className="text-sm font-semibold tabular-nums">{creditBalance}</span>
                </div>
                
                {/* Direct dashboard entry (replaces the user-name dropdown).
                    Account actions — sign out, admin — live in the dashboard,
                    so the header no longer needs the role-check query. */}
                <Button asChild variant="outline" size="sm" className="h-9 gap-2">
                  <Link to={userDashboardPath}>
                    <LayoutDashboard className="h-4 w-4" />
                    <span className="hidden sm:inline">{t.header.dashboard}</span>
                  </Link>
                </Button>
              </>
            ) : (
              <div className="flex items-center gap-2" aria-label="nreport">
                <Button 
                  variant="ghost" 
                  onClick={() => {
                    setAuthDialogMode('login');
                    setAuthDialogOpen(true);
                  }} 
                  size="sm" 
                  className="h-9"
                >
                  {t.header.login}
                </Button>
                <Button 
                  onClick={() => {
                    setAuthDialogMode('register');
                    setAuthDialogOpen(true);
                  }} 
                  size="sm" 
                  className="h-9 hidden sm:inline-flex"
                >
                  {t.header.register}
                </Button>
              </div>
            )}
            
            {/* Mobile Menu */}
            {!isAppRoute && (
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="sm" className="lg:hidden h-9 w-9 p-0">
                    <Menu className="h-4 w-4" />
                    <span className="sr-only">{t.common.toggleMenu}</span>
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[300px] sm:w-[380px] border-l border-border/60">
                  <SheetHeader className="border-b border-border/60 pb-4">
                    <SheetTitle className="flex items-center gap-2 font-brand">
                      <span className="sr-only">nreport</span><BrandLogo />
                    </SheetTitle>
                  </SheetHeader>
                  <nav className="flex flex-col gap-1 mt-6">
                    {navItems.map((item) => (
                      <SheetClose asChild key={item.id}>
                        <button
                          onClick={() => handleNavClick(item.id)}
                          aria-current={activeSection === item.id ? "true" : undefined}
                          className={cn(
                            "rounded-lg px-3 py-3 text-left text-base font-medium transition-colors",
                            activeSection === item.id
                              ? "bg-primary/[0.06] text-primary"
                              : "text-muted-foreground hover:bg-muted hover:text-foreground",
                          )}
                        >
                          {item.label}
                        </button>
                      </SheetClose>
                    ))}
                  </nav>
                </SheetContent>
              </Sheet>
            )}
          </div>
        </div>
      </div>
      
      <AuthDialog 
        open={authDialogOpen} 
        onOpenChange={setAuthDialogOpen}
        onAuthSuccess={() => scrollToPricing()}
        defaultMode={authDialogMode}
      />
    </header>
  );
};